import { createElement } from 'react';
import { defineModule } from '../../engine/module';
import { Money } from '../../engine';
import { InvoiceNumberCell } from './InvoiceNumberCell';
import { LogTimeAction } from './LogTimeAction';
import { TechnicianCostRates } from './TechnicianCostRates';
import { ContractHeader, PrepayAccountsTab } from './ContractPanels';
import { TimesheetHeader, TimesheetEntriesTab } from './TimesheetPanels';
import { LedgerHeader, LedgerEntriesTab } from './LedgerPanels';
import { InviteAction, MemberCell } from './InviteAction';
import { InvoiceDetailScreen } from './InvoiceDetailScreen';
import { BillingRunScreen } from './BillingRunScreen';
import { ReportScreen } from './ReportScreen';
import { SettingsScreen } from './SettingsScreen';
import { IntegrationConnection } from './ConnectedSystemCard';

// The finance module's UI: nav + ScreenDefs. Technicians, contracts, invoices, timesheets,
// the ledger, and the team (members/invites) are engine screens — lists + generic detail
// screens with module header blocks and related-list/custom tabs. The billing run, invoice
// document, reports and workspace settings stay `custom` (each headed `// engine-exception:`,
// counted in .engine-exceptions.json); their reasons are in each file's header.
// The root route ('/') is the home module's cross-module Home; finance KPIs live in its tile.

export const financeModule = defineModule({
  name: 'finance',
  nav: [
    {
      label: 'Billing',
      order: 10,
      items: [
        { label: 'Billing run', to: '/billing', icon: 'zap', key: 'b' },
        { label: 'Invoices', to: '/invoices', icon: 'receipt', key: 'i' },
        { label: 'Contracts & prepay', to: '/contracts', icon: 'file-check', key: 'c' },
        { label: 'Ledger', to: '/ledger', icon: 'book-open', key: 'l' },
      ],
    },
    {
      label: 'Delivery',
      order: 20,
      items: [
        { label: 'Timesheets', to: '/timesheets', icon: 'clock', key: 't' },
        { label: 'Technicians', to: '/technicians', icon: 'wrench', key: 'e' },
      ],
    },
    { label: 'Insight', order: 30, items: [{ label: 'Reports', to: '/reports', icon: 'bar-chart', key: 'r' }] },
    {
      label: 'Organisation',
      area: 'config',
      order: 10,
      items: [
        { label: 'Team', to: '/team', icon: 'users', key: 'm' },
        { label: 'Invites', to: '/invites', icon: 'user-plus' },
        { label: 'Workspace settings', to: '/settings', icon: 'settings', key: 's' },
      ],
    },
    {
      label: 'Finance',
      area: 'config',
      order: 50,
      items: [
        { label: 'Rate cards', to: '/rate-cards', icon: 'layers' },
        { label: 'Labour types', to: '/labour-types', icon: 'tag' },
        { label: 'Tax rates', to: '/taxes', icon: 'percent' },
        { label: 'Items', to: '/items', icon: 'package' },
        { label: 'Integrations', to: '/integrations', icon: 'plug-zap' },
      ],
    },
  ],
  screens: [
    // Configuration → Finance: the rate/tax/item catalogs (generic engine screens).
    { path: '/rate-cards', resource: 'rate-cards', kind: 'list', title: 'Rate cards', subtitle: 'Charge rates per labour type, effective-dated; the default card prices work with no client-specific card.', columns: ['name', 'client_id', 'contract_id', 'is_default', 'currency'], query: { sort: 'name', expand: ['client'] } },
    { path: '/rate-cards/:id', resource: 'rate-cards', kind: 'detail', builtinTabs: ['overview', 'activity'], tabs: [{ key: 'rates', label: 'Charge rates', icon: 'percent', resource: 'charge-rates', filter: { rate_card_id: ':id' }, columns: ['labour_type_id', 'effective_from', 'effective_to', 'rate_minor', 'currency'] }] },
    { path: '/labour-types', resource: 'labour-types', kind: 'list', title: 'Labour types', subtitle: 'Kinds of work a rate card prices (remote, on-site, after-hours…).', columns: ['code', 'name', 'is_default'], query: { sort: 'name' }, inspector: true },
    { path: '/labour-types/:id', resource: 'labour-types', kind: 'detail', builtinTabs: ['overview', 'activity'] },
    { path: '/taxes', resource: 'taxes', kind: 'list', title: 'Tax rates', columns: ['code', 'name', 'rate_percent'], query: { sort: 'code' }, inspector: true },
    { path: '/taxes/:id', resource: 'taxes', kind: 'detail', builtinTabs: ['overview', 'activity'] },
    { path: '/items', resource: 'items', kind: 'list', title: 'Items', subtitle: 'Finance line items (services, fees) with a default price and tax.', columns: ['code', 'name', 'default_price_minor', 'currency', 'tax_ref_id'], query: { sort: 'code' }, inspector: true },
    { path: '/items/:id', resource: 'items', kind: 'detail', builtinTabs: ['overview', 'activity'] },
    { path: '/integrations', resource: 'integrations', kind: 'list', title: 'Integrations', subtitle: 'External systems this workspace links records to (source adapters).', columns: ['name', 'display_name', 'base_url', 'is_active'], query: { sort: 'name' } },
    // The Halo row is where people look for its settings: the connection editor sits on the page
    // (owner/admin; the card renders nothing for anyone else). Also on Workspace settings.
    { path: '/integrations/:id', resource: 'integrations', kind: 'detail', builtinTabs: ['overview'], header: IntegrationConnection },
    // ── Engine screens ───────────────────────────────────────────────────
    {
      path: '/technicians',
      resource: 'technicians',
      kind: 'list',
      title: 'Technicians',
      subtitle: 'Cost & charge rates',
      columns: ['name', 'email', 'employment', 'status', 'cost_rate_minor', 'default_bill_rate_minor'],
      filters: [{ field: 'status', kind: 'chips' }],
      query: { sort: 'name' },
    },
    {
      path: '/technicians/:id',
      resource: 'technicians',
      kind: 'detail',
      tabs: [
        { key: 'cost-rates', label: 'Cost rate history', icon: 'history', component: TechnicianCostRates },
        { key: 'time', label: 'Time', icon: 'timer', resource: 'time-entries', filter: { technician_id: ':id' }, columns: ['occurred_on', 'client_id', 'minutes', 'charge_type', 'note'] },
      ],
    },
    {
      path: '/contracts',
      resource: 'contracts',
      kind: 'list',
      title: 'Contracts & prepay',
      subtitle: 'Recurring agreements and prepaid balances',
      columns: ['client_name', 'ref', 'status', 'cycle', 'period_charge_minor', 'next_invoice_date'],
      filters: [{ field: 'status', kind: 'chips' }],
      form: { fields: ['billing_party_id', 'ref', 'cycle', 'period_charge_minor', 'start_date', 'end_date', 'currency'] },
      detailPath: '/contracts/:id',
    },
    {
      path: '/contracts/:id',
      resource: 'contracts',
      kind: 'detail',
      header: ContractHeader,
      tabs: [
        { key: 'prepay', label: 'Prepay accounts', icon: 'wallet', component: PrepayAccountsTab },
        { key: 'invoices', label: 'Invoices', icon: 'receipt', resource: 'invoices', filter: { contract_id: ':id' }, columns: ['number', 'status', 'issue_date', 'total', 'outstanding'] },
        { key: 'time', label: 'Time', icon: 'timer', resource: 'time-entries', filter: { contract_id: ':id' }, columns: ['occurred_on', 'technician_id', 'minutes', 'charge_type', 'note'] },
      ],
    },
    {
      path: '/invoices',
      resource: 'invoices',
      kind: 'list',
      title: 'Invoices',
      subtitle: 'Drafts, issued, and credit notes',
      columns: [
        { field: 'number', render: (inv) => createElement(InvoiceNumberCell, { row: inv }), width: 170, sortable: true },
        'client_name',
        'status',
        'issue_date',
        'total',
        'outstanding',
      ],
      filters: [{ field: 'status', kind: 'chips' }],
      form: { fields: ['billing_party_id', 'contract_id', 'currency'] },
      detailPath: '/invoices/:id',
    },
    {
      path: '/timesheets',
      resource: 'timesheets',
      kind: 'list',
      title: 'Timesheets',
      subtitle: 'Periods appear automatically as time is logged — weekly or monthly is a workspace setting.',
      columns: ['technician_id', 'period_start', 'period_end', 'status', 'total_minutes', 'locked_at'],
      filters: [{ field: 'status', kind: 'chips' }],
      actions: [{ name: 'log-time', label: 'Log time', scope: 'collection', component: LogTimeAction, openParam: 'log' }],
      create: false,
      detailPath: '/timesheets/:id',
    },
    {
      path: '/timesheets/:id',
      resource: 'timesheets',
      kind: 'detail',
      header: TimesheetHeader,
      tabs: [{ key: 'entries', label: 'Entries', icon: 'list', component: TimesheetEntriesTab }],
    },
    // ── Ledger (read-only system of record) ──────────────────────────────
    {
      path: '/ledger',
      resource: 'ledger-accounts',
      kind: 'list',
      title: 'Ledger',
      subtitle: 'Balance-bearing accounts of record — stored balances, provable from the append-only entries.',
      columns: [
        { field: 'party_name', label: 'Counterparty', render: (a) => createElement('span', { className: a.party_name ? undefined : 'hf-muted' }, (a.party_name as string) ?? '—') },
        'account_type',
        'currency',
        { field: 'running_amount_minor', label: 'Balance', align: 'right', render: (a) => createElement(Money, { minor: a.running_amount_minor as string, currency: a.currency as string, color: true }) },
        'running_quantity',
        'last_seq',
      ],
      create: false,
      detailPath: '/ledger/:id',
    },
    {
      path: '/ledger/:id',
      resource: 'ledger-accounts',
      kind: 'detail',
      header: LedgerHeader,
      builtinTabs: [],
      tabs: [{ key: 'entries', label: 'Entries', icon: 'list', component: LedgerEntriesTab }],
    },
    // ── Team (members + invites) ─────────────────────────────────────────
    {
      path: '/team',
      resource: 'members',
      kind: 'list',
      title: 'Team',
      subtitle: 'Members and pending invites — open a member to change their role or access',
      columns: [
        { field: 'user_email', label: 'Member', render: (m) => createElement(MemberCell, { row: m }) },
        'role',
        'status',
        'created_at',
      ],
      // No detail page: a row opens in the inspector, whose Edit is the generic PATCH form
      // (role / custom role / status); suspend / reactivate / remove sit in the row menu.
      inspector: true,
      create: false,
      actions: [
        { name: 'invite', label: 'Invite someone', scope: 'collection', component: InviteAction, openParam: 'new', placement: 'primary' },
        { name: 'invites', label: 'Pending invites', scope: 'collection', placement: 'toolbar', run: ({ navigate }) => navigate('/invites') },
      ],
    },
    {
      path: '/invites',
      resource: 'invites',
      kind: 'list',
      title: 'Pending invites',
      subtitle: 'Invitations awaiting acceptance — resend or revoke from the row menu',
      columns: ['email', 'role', 'created_at', 'expires_at', 'accepted_at'],
      inspector: true,
      create: false,
      actions: [{ name: 'invite', label: 'Invite someone', scope: 'collection', component: InviteAction, openParam: 'new', placement: 'primary' }],
    },

    // ── Custom screens (engine exceptions; see each file's header) ────────
    { path: '/invoices/:id', resource: 'invoices', kind: 'custom', title: 'Invoice', component: InvoiceDetailScreen },
    { path: '/billing', resource: '', kind: 'custom', title: 'Billing run', component: BillingRunScreen },
    { path: '/reports', resource: '', kind: 'custom', title: 'Reports', component: ReportScreen },
    { path: '/settings', resource: '', kind: 'custom', title: 'Settings', component: SettingsScreen },
  ],
  commands: [
    { id: 'log-time', label: 'Log time…', kind: 'create', icon: 'timer', keywords: 'entry hours minutes track', shortcut: 'g t', run: ({ navigate }) => navigate('/timesheets?log=1') },
    { id: 'billing-run', label: 'Run billing', kind: 'go to', icon: 'zap', keywords: 'unbilled invoice time generate', run: ({ navigate }) => navigate('/billing') },
    { id: 'invite', label: 'Invite a teammate…', kind: 'create', icon: 'user-plus', keywords: 'team member', run: ({ navigate }) => navigate('/team?new=1') },
  ],
});
